/**
 * Sales Calendar Types
 * Event assignments, rep availability, and calendar view types
 */

import type { UUID } from "./common.types";

// =====================================================
// EVENT SALES ASSIGNMENTS
// =====================================================

// "backup" was removed (MSR plan round 9, D12: never used); the database allows only lead/assigned.
export type AssignmentRole = "assigned" | "lead";

export const ASSIGNMENT_ROLE_LABELS: Record<AssignmentRole, string> = {
  assigned: "Assigned",
  lead: "Lead",
};

export interface EventSalesAssignment {
  id: UUID;
  event_id: UUID;
  sales_position_id: UUID;
  role: AssignmentRole;
  /** MSR date model (migration 220): 'all_days' or 'selected' (works only some days of a multi-day event). */
  day_scope?: "all_days" | "selected";
  assigned_by: UUID | null;
  notes: string | null;
  created_at: string;
  updated_at: string;

  // Joined data
  event?: {
    id: UUID;
    title: string;
    start_time: string;
    end_time?: string;
    all_day?: boolean;
    location_name?: string;
    location_address?: Record<string, string> | string | null;
    status?: string;
    type?: string;
  };
  sales_position?: {
    id: UUID;
    title: string;
    assigned_name: string | null;
    position_type: string;
  };
}

export interface CreateEventSalesAssignmentInput {
  event_id: UUID;
  sales_position_id: UUID;
  role?: AssignmentRole;
  notes?: string;
}

// =====================================================
// SALES REP AVAILABILITY
// =====================================================

export type AvailabilityType =
  | "pto"
  | "unavailable"
  | "training"
  | "travel"
  | "out_of_town";

export const AVAILABILITY_TYPE_LABELS: Record<AvailabilityType, string> = {
  pto: "PTO",
  unavailable: "Unavailable",
  training: "Training",
  travel: "Travel",
  out_of_town: "Out of Town",
};

export const AVAILABILITY_TYPE_COLORS: Record<AvailabilityType, string> = {
  pto: "#ef4444", // red-500
  unavailable: "#6b7280", // gray-500
  training: "#8b5cf6", // violet-500
  travel: "#f59e0b", // amber-500
  out_of_town: "#ec4899", // pink-500
};

export interface SalesRepAvailability {
  id: UUID;
  sales_position_id: UUID;
  start_date: string; // DATE
  end_date: string; // DATE
  // SA-5: timed (intra-day) blocks. is_all_day=true → full-day range (start_time/end_time null).
  // is_all_day=false → single-day block (start_date===end_date) with a start_time/end_time range.
  // Times are wall-clock interpreted in America/Chicago (org default).
  is_all_day: boolean;
  start_time: string | null; // "HH:MM" / "HH:MM:SS" when timed, else null
  end_time: string | null;
  availability_type: AvailabilityType;
  title: string | null;
  notes: string | null;
  created_by: UUID | null;
  created_at: string;
  updated_at: string;

  // Joined data
  sales_position?: {
    id: UUID;
    title: string;
    assigned_name: string | null;
  };
}

export interface CreateAvailabilityInput {
  sales_position_id: UUID;
  start_date: string;
  end_date: string;
  // SA-5: omit/true → all-day; false requires start_time+end_time and a single day.
  is_all_day?: boolean;
  start_time?: string | null;
  end_time?: string | null;
  availability_type: AvailabilityType;
  title?: string;
  notes?: string;
}

export interface UpdateAvailabilityInput {
  start_date?: string;
  end_date?: string;
  is_all_day?: boolean;
  start_time?: string | null;
  end_time?: string | null;
  availability_type?: AvailabilityType;
  title?: string;
  notes?: string;
}

// =====================================================
// CALENDAR VIEW TYPES
// =====================================================

export type CalendarEventType = "event" | "availability" | "calendar_request";

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: {
    type: CalendarEventType;
    repName: string;
    repColor: string;
    positionId: string;
    originalData: EventSalesAssignment | SalesRepAvailability | unknown;
    // For availability blocks
    availabilityType?: AvailabilityType;
    // For events
    eventStatus?: string;
    eventType?: string;
    locationName?: string;
    locationAddress?: string;
  };
}

export interface SalesCalendarFilters {
  selectedPositionIds: UUID[];
  dateRange: { start: string; end: string };
  showEvents: boolean;
  showAvailability: boolean;
  showCalendarRequests: boolean;
}

// =====================================================
// EVENT TYPE COLOR PALETTE
// =====================================================

export const EVENT_TYPE_COLORS: Record<string, string> = {
  show: "#3b82f6", // blue-500
  class: "#10b981", // emerald-500
  table: "#0ea5e9", // sky-500
  seminar: "#8b5cf6", // violet-500
  conference: "#6366f1", // indigo-500
  speaking_engagement: "#a855f7", // purple-500
  webinar: "#06b6d4", // cyan-500
  gun_show: "#f97316", // orange-500
  private_event: "#d946ef", // fuchsia-500
  community_event: "#14b8a6", // teal-500
  meeting: "#64748b", // slate-500
  training: "#eab308", // yellow-500
  call: "#78716c", // stone-500
  social: "#f43f5e", // rose-500
  other: "#9ca3af", // gray-400
};

export const EVENT_TYPE_LABELS: Record<string, string> = {
  show: "Show",
  class: "Class",
  table: "Table",
  seminar: "Seminar / Workshop / GSW",
  conference: "Conference / Expo",
  speaking_engagement: "Speaking Engagement",
  webinar: "Webinar",
  gun_show: "Gun Show",
  private_event: "Private Event",
  community_event: "Community Event",
  meeting: "Meeting",
  training: "Training",
  call: "Call",
  social: "Social",
  other: "Other",
};

export const EVENT_TYPE_DEFAULT_COLOR = "#9ca3af";

export function getEventTypeColor(eventType?: string): string {
  if (!eventType) {
    return EVENT_TYPE_DEFAULT_COLOR;
  }
  return EVENT_TYPE_COLORS[eventType] || EVENT_TYPE_DEFAULT_COLOR;
}

// Rep color palette (used for calendar requests and rep filter dots)
export const REP_COLORS = [
  "#3b82f6", // blue-500
  "#10b981", // emerald-500
  "#f97316", // orange-500
  "#8b5cf6", // violet-500
  "#06b6d4", // cyan-500
  "#f43f5e", // rose-500
  "#84cc16", // lime-500
  "#d946ef", // fuchsia-500
  "#14b8a6", // teal-500
  "#eab308", // yellow-500
  "#6366f1", // indigo-500
  "#78716c", // stone-500
];

export function getRepColor(index: number): string {
  return REP_COLORS[index % REP_COLORS.length];
}
